import { useEffect, useMemo, useRef, useState } from 'react';
import { Viewer } from '@photo-sphere-viewer/core';
import '@photo-sphere-viewer/core/index.css';

/**
 * Página standalone para comparar visualmente las estrategias del experimento
 * de stitching 360 (B / C / D / E + variantes). Cargá un link y mirá cómo
 * se ve adentro del visor real de la app.
 *
 * URLs hardcodeadas del CDN público de fal.ai. Usar `panorama` directo de PSV
 * (CORS abierto en v3b.fal.media). Si una URL llegase a fallar por CORS, hay
 * fallback a fetch + blob URL.
 */

interface Sample {
  label: string;
  url: string;
  notes?: string;
  /**
   * Si la imagen NO es un equirectangular completo (cubre menos de 360°x180°),
   * pasamos panoData indicando que es un CROP del panorama esférico conceptual.
   * PSV no fuerza wraparound y muestra los bordes en negro, eliminando la
   * falsa costura que aparece cuando se trata una imagen parcial como esférica.
   *
   * Cálculo: si la imagen cubre H° horizontales y V° verticales,
   *   full_width = imgWidth * 360 / H
   *   full_height = imgHeight * 180 / V
   *   cropped_x = (full_width - imgWidth) / 2  (centrado horizontal)
   *   cropped_y = (full_height - imgHeight) / 2  (centrado vertical)
   */
  panoData?: {
    fullWidth: number;
    fullHeight: number;
    croppedWidth: number;
    croppedHeight: number;
    croppedX: number;
    croppedY: number;
  };
}

const SAMPLES: Sample[] = [
  {
    label: '🎯 G2 PARCIAL (270°×90°, sin wraparound forzado) — visor LIMITADO',
    url: 'https://v3b.fal.media/files/b/0a978827/aWsCvBRytNnP2s692SY_d_G2_plus_E.png',
    notes: 'Misma imagen que G2+E pero al visor le decimos "esto es un crop, no completes en esfera". Sin falsa costura.',
    // 1520×704 → asumimos cubre 270° horizontal × 90° vertical
    panoData: {
      fullWidth: 2026,
      fullHeight: 1408,
      croppedWidth: 1520,
      croppedHeight: 704,
      croppedX: 253,
      croppedY: 352,
    },
  },
  {
    label: '🎯 G1 PARCIAL (360°×90°, sin polos) — visor LIMITADO',
    url: 'https://v3b.fal.media/files/b/0a978827/vdsMu5X20E46jAX2KiK_N_G1_plus_E.png',
    notes: 'G1+E tratada como cubre 360° horizontal pero solo 90° vertical → bordes negros sin deformación polar.',
    // 1456×720 → cubre 360° hor × 90° vert
    panoData: {
      fullWidth: 1456,
      fullHeight: 1440,
      croppedWidth: 1456,
      croppedHeight: 720,
      croppedX: 0,
      croppedY: 360,
    },
  },
  {
    label: '🚀 G2 + E (prompt VAST + ultrawide pedido 4:1) — $0.30, ratio 2.16:1, seam 0',
    url: 'https://v3b.fal.media/files/b/0a978827/aWsCvBRytNnP2s692SY_d_G2_plus_E.png',
    notes: 'Probando: prompt con énfasis brutal en amplitud + zonas a 5-7m + mucho piso visible.',
  },
  {
    label: '🚀 G1 + E (prompt VAST + ratio 2:1 puro) — $0.30, seam 0',
    url: 'https://v3b.fal.media/files/b/0a978827/vdsMu5X20E46jAX2KiK_N_G1_plus_E.png',
    notes: 'Mismo prompt VAST pero forzando 2:1. Ratio limpio sin polos negros.',
  },
  {
    label: 'G1 solo (sin PIL, ver crudo)',
    url: 'https://v3b.fal.media/files/b/0a978827/vS9CUC45jbPuIBgK9bKzH_G1.png',
  },
  {
    label: 'G2 solo (sin PIL, ver crudo)',
    url: 'https://v3b.fal.media/files/b/0a978827/09C6ZyNLvUtmir0FuQCTQ_G2.png',
  },
  {
    label: 'F1a + E (prompt anterior, NO insistía en amplitud) — $0.15',
    url: 'https://v3b.fal.media/files/b/0a9787d8/vBcjTsHHefCUrBYenLLDR_F1a_plus_E.png',
    notes: 'Tu feedback fue: "todo muy chiquito como una sola habitación". G1/G2 corrige esto.',
  },
  {
    label: 'F1a solo (nano-banana custom size, sin PIL final)',
    url: 'https://v3b.fal.media/files/b/0a9787d8/dheOZa1izDH9C-B0q8UVI_F1a.png',
    notes: 'Comparar contra F1a+E para ver el efecto del blend.',
  },
  {
    label: 'F1b (mismo prompt pero con preset 2K + 21:9) — ratio 2.36:1 → polos negros',
    url: 'https://v3b.fal.media/files/b/0a9787d8/aVrx0C8O-brNsnzLcRi5a_F1b.png',
    notes: 'Para confirmar que el preset enum produce ratio NO 2:1.',
  },
  {
    label: 'D + E (FLUX i2i + LoRA equirec + PIL blend) — $0.05, ratio 2:1, seam 0',
    url: 'https://v3b.fal.media/files/b/0a977ca8/-4bJnZjJ3F97moZgMCdaF_D_flux_plus_E.png',
    notes: 'Composición fluida pero alucina aparatos.',
  },
  {
    label: 'D solo (sin PIL) — seam roto delta 44',
    url: 'https://v3b.fal.media/files/b/0a977ca7/JBJOCIfPSM1sTNgoA_ui9_D_flux_equirec.png',
    notes: 'Misma D pero sin la capa final de blend.',
  },
  {
    label: '🥈 B + E (outpaint + rotate-and-fix + PIL) — $0.30, ratio 2.36:1, seam 0',
    url: 'https://v3b.fal.media/files/b/0a977ca6/eeL5gjC6CRG8YkMhxgVEu_B_FINAL_plus_E.png',
    notes: 'Más caro. Le faltan zonas explícitas pero geometría OK.',
  },
  {
    label: 'B solo (sin PIL final) — seam delta 10',
    url: 'https://v3b.fal.media/files/b/0a977ca6/vkGQfL7KkjgwbII9fuZjc_B_FINAL.png',
  },
  {
    label: 'B1 raw (outpaint puro de #ref1) — seam ROTO delta 204',
    url: 'https://v3b.fal.media/files/b/0a977ca6/XCJ0woPkVDwFWmp2fOo5g_B1_outpaint.png',
    notes: 'Para confirmar visualmente que sin rotate-and-fix queda mal.',
  },
  {
    label: '📌 E solo (PIL blend sobre #ref8 viejo) — $0, seam 0',
    url: 'https://v3b.fal.media/files/b/0a977ca8/YI246s0-1uwOX3pENQUcR_E_ref8_blended.png',
    notes: 'Tu candidata: cierra seam matemáticamente sin tocar geometría.',
  },
  {
    label: 'C cubemap py360convert SIN feather',
    url: 'https://v3b.fal.media/files/b/0a978703/Te5-T4BPlWuxvghrsKma8_C_PRO.png',
    notes: 'Stitching pro; uniones verticales entre caras visibles.',
  },
  {
    label: 'C cubemap py360convert + feather 30px',
    url: 'https://v3b.fal.media/files/b/0a978703/q_lC0IH-aJ-rrACW2Idgl_C_PRO_feathered.png',
    notes: 'Mismo pero con suavizado en uniones.',
  },
  {
    label: 'C ingenuo (script casero, sin py360convert)',
    url: 'https://v3b.fal.media/files/b/0a977ca7/PDBK7KwdoD3YMzE69_Gob_C_FINAL.png',
    notes: 'Para comparar — peor que py360convert.',
  },
];

export function Test360Page() {
  const [selected, setSelected] = useState(0);
  const [customUrl, setCustomUrl] = useState('');
  const [zoomLvl, setZoomLvl] = useState(50);
  const containerRef = useRef<HTMLDivElement>(null);
  const viewerRef = useRef<Viewer | null>(null);

  const activeSample = useMemo(() => {
    if (customUrl.trim()) {
      return { label: 'custom', url: customUrl.trim() } as Sample;
    }
    return SAMPLES[selected];
  }, [customUrl, selected]);
  const activeUrl = activeSample.url;
  const activePanoData = activeSample.panoData;

  useEffect(() => {
    if (!containerRef.current || !activeUrl) return;
    let cancelled = false;
    let createdUrl: string | null = null;
    (async () => {
      try {
        // Intentar pasar URL directa primero (más rápido).
        // Si CORS falla, PSV tira un error que se ve en consola — usamos fetch.
        const res = await fetch(activeUrl, { mode: 'cors' });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const blob = await res.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        const viewer = new Viewer({
          container: containerRef.current!,
          panorama: createdUrl,
          // Si es panorama parcial le pasamos panoData → PSV no fuerza wraparound
          // y rellena los bordes con el background color del visor (negro), lo que
          // elimina la falsa costura.
          ...(activePanoData ? { panoData: activePanoData } : {}),
          navbar: ['zoom', 'move', 'fullscreen'],
          defaultZoomLvl: 50,
          minFov: 30,
          maxFov: 100,
          mousewheel: true,
          touchmoveTwoFingers: false,
        });
        viewerRef.current = viewer;
        const handler = (e: unknown) => {
          const z = (e as { zoomLevel?: number }).zoomLevel;
          if (typeof z === 'number') setZoomLvl(z);
        };
        viewer.addEventListener('zoom-updated', handler as never);
      } catch (err) {
        console.error('Failed to load panorama:', err);
      }
    })();
    return () => {
      cancelled = true;
      if (viewerRef.current) {
        viewerRef.current.destroy();
        viewerRef.current = null;
      }
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [activeUrl, activePanoData]);

  useEffect(() => {
    if (viewerRef.current) viewerRef.current.zoom(zoomLvl);
  }, [zoomLvl]);

  return (
    <div className="fixed inset-0 bg-slate-950 text-slate-100 flex flex-col">
      <header className="border-b border-slate-800 px-4 py-3 space-y-2 bg-slate-900">
        <div className="flex items-center gap-3">
          <div className="text-base font-semibold">🧪 Test 360 — comparador de estrategias</div>
          <a
            href="/projects"
            className="ml-auto text-xs text-slate-400 hover:text-vision underline"
          >
            ← volver a la app
          </a>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selected}
            onChange={(e) => {
              setSelected(Number(e.target.value));
              setCustomUrl('');
            }}
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-vision"
          >
            {SAMPLES.map((s, i) => (
              <option key={i} value={i}>
                {s.label}
              </option>
            ))}
          </select>
          <input
            type="url"
            value={customUrl}
            onChange={(e) => setCustomUrl(e.target.value)}
            placeholder="o pegá una URL custom (sobreescribe el dropdown)"
            className="flex-1 bg-slate-950 border border-slate-700 rounded px-2 py-1.5 text-sm focus:outline-none focus:border-vision font-mono text-xs"
          />
        </div>
        {SAMPLES[selected].notes && !customUrl && (
          <div className="text-xs text-slate-400 italic">{SAMPLES[selected].notes}</div>
        )}
      </header>
      <div className="flex-1 relative">
        <div ref={containerRef} className="absolute inset-0" />
        <div className="absolute top-3 right-3 left-3 sm:left-auto z-10 bg-slate-950/85 backdrop-blur-sm rounded-lg px-3 py-2.5 flex items-center gap-3 border border-slate-700">
          <span className="text-[11px] text-slate-400 uppercase tracking-wide whitespace-nowrap">
            FOV
          </span>
          <input
            type="range"
            min={0}
            max={100}
            value={zoomLvl}
            onChange={(e) => setZoomLvl(Number(e.target.value))}
            className="flex-1 sm:w-40 sm:flex-none accent-vision h-2 touch-manipulation"
          />
          <span className="text-[11px] text-slate-300 w-8 text-right tabular-nums">{zoomLvl}</span>
        </div>
      </div>
    </div>
  );
}
